import Validator from './validator';
import UiTracking from '../tracking/ui-tracking';
import { ErrorMessages, PasswordRestrictions } from './interfaces';

export default class EmailPassword {
	private readonly form: HTMLFormElement;
	private inputContainer: HTMLElement;
	private passwordRestrictionInfo: HTMLElement;
	private emailInput: HTMLInputElement;
	private passwordInput: HTMLInputElement;
	private errorMessages: ErrorMessages;
	private passwordRestrictions: PasswordRestrictions;
	private uiTrackingEnabled: boolean;
	private errorCountEmail: number;
	private errorCountPasswordEmpty: number;
	private errorCountPasswordInvalid: number;

	public constructor(container: HTMLFormElement) {
		this.form = container;
		this.inputContainer = this.form.querySelector('.email-password');
		this.uiTrackingEnabled = this.form.dataset.biEnabled === 'true';
		this.errorCountEmail = 0;
		this.errorCountPasswordEmpty = 0;
		this.errorCountPasswordInvalid = 0;

		this.getDomElements();
		this.setEventListener();
		this.initUiTracking();
	}

	private initUiTracking(): void {
		if (!this.uiTrackingEnabled) {
			return;
		}

		this.emailInput.addEventListener(
			'input',
			(event: Event) =>
				UiTracking.trackUIEvent('registration-form', 'email-input', event.type),
			{
				once: true,
			}
		);

		this.passwordInput.addEventListener(
			'input',
			(event: Event) =>
				UiTracking.trackUIEvent('registration-form', 'password-input', event.type),
			{
				once: true,
			}
		);
	}

	private static updateInfoBox(event: FocusEvent, box: HTMLElement): void {
		box.classList.toggle('is-visible', event.type === 'focus');
	}

	private getDomElements(): void {
		this.emailInput = this.form.querySelector('.reg-form-email');
		this.passwordInput = this.form.querySelector('.reg-form-password');
		this.passwordRestrictionInfo = this.form.querySelector('.password-restriction-info');

		this.errorMessages = {
			regEmailError: this.form.querySelector('.email-error'),
			regPasswordEmptyError: this.form.querySelector('.password-empty-error'),
			regPasswordNotValidError: this.form.querySelector('.password-invalid-error'),
		};

		this.passwordRestrictions = {
			minCharacters: this.form.querySelector('.min-characters'),
			lettersAndNumbers: this.form.querySelector('.letters-and-numbers'),
			partOfEmail: this.form.querySelector('.part-of-email'),
		};
	}

	private setEventListener(): void {
		this.emailInput.addEventListener('keyup', () => this.onEmailInput());
		this.emailInput.addEventListener('change', () => this.onEmailInput());

		this.passwordInput.addEventListener('keyup', () => this.onPasswordInput());
		this.passwordInput.addEventListener('change', () => this.onPasswordInput());
		this.passwordInput.addEventListener('focus', (event: FocusEvent) => {
			EmailPassword.updateInfoBox(event, this.passwordRestrictionInfo);
			EmailPassword.hideError(this.errorMessages.regPasswordEmptyError);
			EmailPassword.hideError(this.errorMessages.regPasswordNotValidError);
		});
		this.passwordInput.addEventListener('blur', (event: FocusEvent) =>
			EmailPassword.updateInfoBox(event, this.passwordRestrictionInfo)
		);
	}

	public enableUi(): void {
		this.inputContainer.inert = false;
	}

	private onEmailInput(): void {
		if (Validator.isValidEmail(this.emailInput.value)) {
			EmailPassword.hideError(this.errorMessages.regEmailError);
			this.emailInput.classList.remove('error');
			this.emailInput.setAttribute('aria-invalid', 'false');
		}
	}

	private onPasswordInput(): void {
		EmailPassword.hideError(this.errorMessages.regPasswordNotValidError);
		EmailPassword.hideError(this.errorMessages.regPasswordEmptyError);
		this.passwordInput.classList.remove('error');
		this.passwordInput.setAttribute('aria-invalid', 'false');

		if (Validator.isValidPasswordLength(this.passwordInput.value)) {
			this.passwordRestrictions.minCharacters.classList.add('success');
		} else {
			this.passwordRestrictions.minCharacters.classList.remove('success');
		}

		if (Validator.hasLettersAndNumbers(this.passwordInput.value)) {
			this.passwordRestrictions.lettersAndNumbers.classList.add('success');
		} else {
			this.passwordRestrictions.lettersAndNumbers.classList.remove('success');
		}

		if (Validator.isNotPartOfEmail(this.emailInput.value, this.passwordInput.value)) {
			this.passwordRestrictions.partOfEmail.classList.add('success');
		} else {
			this.passwordRestrictions.partOfEmail.classList.remove('success');
		}
	}

	public validate(): boolean {
		this.emailInput.classList.remove('error');
		this.passwordInput.classList.remove('error');

		let isValid: boolean = true;
		const isEmptyPassword: boolean = this.passwordInput.value.length === 0;
		const isInvalidPasswordLength: boolean = !Validator.isValidPasswordLength(
			this.passwordInput.value
		);
		const hasNotLettersAndNumbers: boolean = !Validator.hasLettersAndNumbers(
			this.passwordInput.value
		);
		const isPartOfEmail: boolean = !Validator.isNotPartOfEmail(
			this.emailInput.value,
			this.passwordInput.value
		);

		if (!Validator.isValidEmail(this.emailInput.value)) {
			isValid = false;
			EmailPassword.showError(this.errorMessages.regEmailError);
			this.emailInput.classList.add('error');
			this.emailInput.setAttribute('aria-invalid', 'true');

			if (this.uiTrackingEnabled) {
				UiTracking.trackUIEvent(
					'registration-form',
					'form',
					'validation-error',
					'email',
					this.errorCountEmail.toString()
				);

				this.errorCountEmail++;
			}
		}

		if (isEmptyPassword) {
			isValid = false;
			EmailPassword.showError(this.errorMessages.regPasswordEmptyError);
			this.passwordInput.classList.add('error');
			this.passwordInput.setAttribute('aria-invalid', 'true');

			if (this.uiTrackingEnabled) {
				UiTracking.trackUIEvent(
					'registration-form',
					'form',
					'validation-error',
					'password-empty',
					this.errorCountPasswordEmpty.toString()
				);
				this.errorCountPasswordEmpty++;
			}
		} else if (isInvalidPasswordLength || hasNotLettersAndNumbers || isPartOfEmail) {
			isValid = false;
			EmailPassword.showError(this.errorMessages.regPasswordNotValidError);
			this.passwordInput.classList.add('error');
			this.passwordInput.setAttribute('aria-invalid', 'true');

			if (this.uiTrackingEnabled) {
				UiTracking.trackUIEvent(
					'registration-form',
					'form',
					'validation-error',
					'password-invalid',
					this.errorCountPasswordInvalid.toString()
				);
				this.errorCountPasswordInvalid++;
			}
		}

		return isValid;
	}

	private static showError(errorType: HTMLElement): void {
		errorType.classList.add('is-visible');
		errorType.textContent = errorType.dataset.errorText;
	}

	private static hideError(errorType: HTMLElement): void {
		errorType.classList.remove('is-visible');
		errorType.textContent = '';
	}

	public hideAllErrors(): void {
		this.emailInput.classList.remove('error');
		this.passwordInput.classList.remove('error');
		this.passwordRestrictions.minCharacters.classList.remove('is-visible');
		this.passwordRestrictions.lettersAndNumbers.classList.remove('is-visible');
		this.passwordRestrictions.partOfEmail.classList.remove('is-visible');
		this.errorMessages.regEmailError.classList.remove('is-visible');
		this.errorMessages.regPasswordEmptyError.classList.remove('is-visible');
		this.errorMessages.regPasswordNotValidError.classList.remove('is-visible');
	}
}
